import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flag, BadgeCheck, StickyNote, ChevronDown, ChevronUp, Send, Landmark } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

export const OfficialBadge = () => (
  <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wide text-emerald-700" data-testid="official-source-badge">
    <BadgeCheck className="w-3.5 h-3.5" /> Official Government Source
  </span>
);

export const LastVerified = ({ date, source }) => (
  <p className="text-xs text-muted-foreground" data-testid="last-verified">
    Information last verified: <span className="font-mono">{date || "—"}</span>
    {source && <> · Source: {source}</>}
  </p>
);

const ISSUE_TYPES = [
  ["incorrect_information", "Incorrect information"],
  ["name_changed", "Name / website changed"],
  ["permanently_closed", "Mission closed"],
  ["temporary_closure", "Temporarily closed"],
  ["new_service", "New mission / service"],
  ["other", "Other"],
];

export function SuggestCorrection({ entityType, entityId, entityName }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [issue, setIssue] = useState("incorrect_information");
  const [detail, setDetail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (detail.trim().length < 5) { toast.error("Please describe the correction (at least 5 characters)"); return; }
    setBusy(true);
    try {
      await api.post("/transport/reports", { mode: "visas", entity_type: entityType, entity_id: entityId, issue_type: issue, detail: detail.trim() });
      toast.success("Thank you — your correction entered the verification queue");
      setOpen(false); setDetail("");
    } catch (e) {
      toast.error(e?.response?.status === 401 ? "Please sign in to suggest a correction" : "Could not submit correction");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`suggest-correction-${entityType}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid="suggest-correction-toggle"
        className="inline-flex items-center gap-1.5 text-sm font-700 text-zinc-600 hover:text-primary transition-colors">
        <Flag className="w-4 h-4" /> Suggest a correction {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <div className="mt-3 border border-zinc-300 bg-white p-4 max-w-lg">
          {!user && <p className="text-sm text-amber-700 mb-3">Please sign in to submit — corrections are reviewed by our editors before publication.</p>}
          <p className="text-xs text-muted-foreground mb-3">Reporting: <span className="font-600">{entityName}</span>. Corrections are never published automatically — they go to the admin verification queue.</p>
          <select value={issue} onChange={(e) => setIssue(e.target.value)} data-testid="correction-issue-select"
            className="w-full border border-zinc-300 bg-white px-3 py-2 text-sm mb-2 focus:outline-none">
            {ISSUE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <textarea value={detail} onChange={(e) => setDetail(e.target.value)} rows={3} data-testid="correction-detail-input"
            placeholder="What's wrong and what's the correct information? Include an official source link if possible."
            className="w-full border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none" />
          <button onClick={submit} disabled={busy || !user} data-testid="correction-submit"
            className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-white text-sm font-600 hover:bg-blue-700 disabled:opacity-50">
            <Send className="w-3.5 h-3.5" /> {busy ? "Submitting…" : "Submit for review"}
          </button>
        </div>
      )}
    </div>
  );
}

export function PublicNotes({ notes }) {
  if (!notes?.length) return null;
  return (
    <div className="border border-sky-200 bg-sky-50 p-4 space-y-3" data-testid="public-editor-notes">
      {notes.map((n) => (
        <div key={n.id} className="text-sm text-sky-900">
          <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wide text-sky-700 mb-1">
            <StickyNote className="w-3.5 h-3.5" /> Editor's note · {new Date(n.created_at).toLocaleDateString()}
          </div>
          {n.text}
        </div>
      ))}
    </div>
  );
}

export function EmbassiesInCity({ city, countryIso }) {
  const [missions, setMissions] = useState(null);
  useEffect(() => {
    api.get("/visas/missions", { params: { city, host: countryIso || undefined } })
      .then((r) => setMissions(r.data.items)).catch(() => setMissions([]));
  }, [city, countryIso]);
  if (!missions?.length) return null; // §15: no empty lists for SEO
  return (
    <section className="py-4" data-testid="city-embassies-section">
      <h2 className="font-heading font-900 text-[#0e7490] text-2xl mb-2 flex items-center gap-2">
        <Landmark className="w-6 h-6" /> Embassies &amp; Consulates in {city}
      </h2>
      <p className="text-sm text-muted-foreground mb-5">Verified diplomatic missions located in this city. <Link to="/visas" className="text-primary font-700 hover:underline">Passports &amp; visas hub →</Link></p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {missions.map((m) => (
          <Link key={m.id} to={`/visas/missions/${m.id}`} data-testid={`city-mission-${m.id}`}
            className="border border-zinc-300 bg-white p-4 hover:border-[#0e7490] hover:-translate-y-px transition-[transform,border-color]">
            <div className="font-heading font-700 text-sm leading-snug">{m.name}</div>
            <div className="text-xs text-muted-foreground mt-1">{m.address}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function AdminNotesPanel({ entityType, entityId }) {
  const { user } = useAuth();
  const isMod = user && ["moderator", "admin"].includes(user.role);
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState("");
  const [pub, setPub] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isMod) api.get(`/visas/admin-notes/${entityType}/${entityId}`).then((r) => setNotes(r.data.items)).catch(() => {});
  }, [isMod, entityType, entityId]);

  if (!isMod) return null;

  const add = async () => {
    if (text.trim().length < 5) return;
    setBusy(true);
    try {
      const { data } = await api.post("/visas/admin-notes", { entity_type: entityType, entity_id: entityId, text: text.trim(), public: pub });
      setNotes([data, ...(notes || [])]); setText(""); setPub(false);
      toast.success("Admin note saved");
    } catch { toast.error("Could not save note"); }
    finally { setBusy(false); }
  };

  const togglePublic = async (n) => {
    try {
      const { data } = await api.put(`/visas/admin-notes/${n.id}`, { public: !n.public });
      setNotes(notes.map((x) => x.id === n.id ? data : x));
    } catch { toast.error("Could not update note"); }
  };

  return (
    <div className="border border-dashed border-violet-400 bg-violet-50/50 p-5" data-testid="admin-notes-panel">
      <h3 className="font-heading font-700 text-sm uppercase tracking-wide text-violet-800 mb-3">Admin notes (editors only)</h3>
      <div className="flex gap-2 mb-1.5">
        <input value={text} onChange={(e) => setText(e.target.value)} data-testid="admin-note-input"
          placeholder="Internal note — e.g. 'Official website changed, verified against MFA site'"
          className="flex-1 border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none" />
        <button onClick={add} disabled={busy} data-testid="admin-note-save"
          className="px-4 py-2 bg-violet-700 text-white text-sm font-600 hover:bg-violet-800 disabled:opacity-50">Add</button>
      </div>
      <label className="flex items-center gap-1.5 text-xs text-violet-800 mb-3 cursor-pointer">
        <input type="checkbox" checked={pub} onChange={(e) => setPub(e.target.checked)} data-testid="admin-note-public-toggle" className="accent-violet-700" />
        Publish on profile (visible to travelers)
      </label>
      {(notes || []).map((n) => (
        <div key={n.id} className="border-t border-violet-200 py-2 text-sm" data-testid={`admin-note-${n.id}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-mono text-violet-700">{n.author_name} · {new Date(n.created_at).toLocaleString()}
              {n.revisions?.length > 0 && ` · ${n.revisions.length} revision(s)`}</span>
            <button onClick={() => togglePublic(n)} data-testid={`admin-note-toggle-${n.id}`}
              className={`text-xs font-mono uppercase px-1.5 py-0.5 border ${n.public ? "border-emerald-400 text-emerald-700 bg-emerald-50" : "border-zinc-300 text-zinc-600"}`}>
              {n.public ? "Public" : "Internal"}
            </button>
          </div>
          <p className="text-zinc-800 mt-0.5">{n.text}</p>
        </div>
      ))}
    </div>
  );
}
